import type { TournamentApplyInput } from "../../types/tournament.types";
import { apiClient } from "../axios.config";
import type {
  TournamentQrCodeParams,
  TournamentQrParams,
} from "../dto/request/tournament.request";
import type { Response } from "../dto/response/common.response";
import type {
  ApplyQueueEntryResponse,
  ApplyQueueStatusResponse,
  OvertimeScoreCardResponse,
  ScoreCalendarListResponse,
  ScoreCardListResponse,
  ScoreCardViewResponse,
  ScoreOrderListResponse,
  TournamentCalendarListResponse,
  TournamentCalendarsListResponse,
  TournamentDetailResponse,
  TournamentListParams,
  TournamentListResponse,
  TournamentQrCodeResponse,
  TournamentQrListItemResponse,
  TournamentTermsResponse,
} from "../dto/response/torunament.response";

const model = "/v1/tournament";

/**
 * Tournament Service API
 * @module TournamentService
 * @description 대회 관련 API
 * @author DongGyun Yoo
 * @since 1.0.0
 */

export async function getTournamentsList(
  params: TournamentListParams = {},
): Promise<Response<TournamentListResponse>> {
  const { data } = await apiClient.get(`${model}/list`, { params });
  return data;
}

export async function getTournamentDetails(
  tournamentId: string | number,
): Promise<Response<TournamentDetailResponse>> {
  const { data } = await apiClient.get(`${model}/${tournamentId}/info`);
  return data;
}

export async function getTournamentCalendarsList(
  tournamentId: string | number,
  date: string, //YYYY-MM-DD
): Promise<Response<TournamentCalendarsListResponse[]>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/calendars?date=${date}`,
  );
  return data;
}

export async function getTournamentCalendarList(
  tournamentId: string | number,
): Promise<Response<TournamentCalendarListResponse[]>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/calendar-list`,
  );
  return data;
}

export async function getScoreCalendarList(
  tournamentId: string | number,
): Promise<Response<ScoreCalendarListResponse>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/score-calendar-list`,
  );
  return data;
}

export async function getScoreOrderList(
  tournamentId: string | number,
  calendarId: string | number,
): Promise<Response<ScoreOrderListResponse>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/score-order-list?calendarId=${calendarId}`,
  );
  return data;
}

export async function getRoundScoreOrderList(
  tournamentId: string | number,
  roundId?: string | number | null,
  gender?: 0 | 1,
): Promise<Response<ScoreOrderListResponse>> {
  const params = {
    ...(roundId != null ? { roundId } : {}),
    ...(gender != null ? { gender } : {}),
  };
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/round/score-order-list`,
    { params },
  );
  return data;
}

export async function getScoreCardView(
  tournamentId: string | number,
  calendarId: string | number,
  scoreCardId: string | number,
): Promise<Response<ScoreCardViewResponse>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/calendar/${calendarId}/score-card/${scoreCardId}/view`,
  );
  return data;
}

export async function getUserScoreCards(
  tournamentId: string | number,
  calendarId: string | number,
  userId: string | number,
  roundId?: string | number | null,
): Promise<Response<ScoreCardListResponse>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/calendar/${calendarId}/user/${userId}/score-cards`,
    { params: roundId != null ? { roundId } : undefined },
  );
  return data;
}

export async function getTournamentTerms(
  tournamentId: string | number,
): Promise<Response<{ termsList: TournamentTermsResponse[] }>> {
  const { data } = await apiClient.get(`${model}/${tournamentId}/terms`);
  return data;
}

/** 대회 현장 인증 QR 목록 */
export async function getTournamentQrList(
  tournamentId: string | number,
  params: TournamentQrParams,
): Promise<Response<TournamentQrListItemResponse[]>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/qr-list`,
    { params },
  );
  return data;
}

/** 대회 현장 인증 QR 코드 생성 */
export async function postTournamentQrCode(
  tournamentId: string | number,
  params: TournamentQrCodeParams,
): Promise<Response<TournamentQrCodeResponse>> {
  const { data } = await apiClient.post(
    `${model}/${tournamentId}/qr-code`,
    undefined,
    { params },
  );
  return data;
}

export async function TournamentApply(
  tournamentId: string | number,
  form: TournamentApplyInput,
  files: File[],
): Promise<Response<number>> {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append("fileList", file));
  formData.append(
    "data",
    new Blob([JSON.stringify(form)], { type: "application/json" }),
  );

  const { data } = await apiClient.post(
    `${model}/${tournamentId}/apply`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data;
}

export async function TournamentApplyInfo(tournamentId: string) {
  const { data } = await apiClient.get(`${model}/${tournamentId}/apply-info`);
  return data;
}

export async function TournamentApplyUpdate(
  tournamentId: string | number,
  form: TournamentApplyInput,
  files: File[],
) {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append("fileList", file));
  formData.append(
    "data",
    new Blob([JSON.stringify(form)], { type: "application/json" }),
  );

  const { data } = await apiClient.put(
    `${model}/${tournamentId}/apply`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data;
}

export async function TournamentCancel(
  tournamentId: string | number,
  cancelReason: string,
): Promise<Response<string>> {
  const { data } = await apiClient.put(`${model}/${tournamentId}/cancel`, {
    cancelReason,
  });
  return data;
}

/** 내 연장전 차수 요약 */
export async function getOvertimeSummaryList(
  tournamentId: string | number,
  roundId: string | number,
): Promise<Response<OvertimeScoreCardResponse>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/overtime-summary?roundId=${roundId}`,
  );
  return data;
}

/** 접수 대기열 진입 — 접수 화면 진입 시 1회 호출한다 */
export async function postApplyQueueEntry(
  tournamentId: string | number,
): Promise<Response<ApplyQueueEntryResponse>> {
  const { data } = await apiClient.post(
    `${model}/${tournamentId}/apply-queue/entry`,
  );
  return data;
}

/** 접수 대기열 상태 폴링 — 이 호출 자체가 대기 생존 신고다 */
export async function getApplyQueueStatus(
  tournamentId: string | number,
): Promise<Response<ApplyQueueStatusResponse>> {
  const { data } = await apiClient.get(
    `${model}/${tournamentId}/apply-queue/status`,
  );
  return data;
}
